// Phaser-specific binding helpers. Pulled into a sub-export so the core i18n
// runtime has no Phaser dependency for non-Phaser games (Babylon, Pixi).

import type Phaser from 'phaser';
import { fontFamilyForScript } from './font-stacks';
import { LANGUAGES } from './languages';
import type { Direction, I18n, LangCode, SharedKey } from './index';

/**
 * Binds a Phaser.Text to a translation key. The text content, RTL flag, and
 * script-aware font family are updated on every language change. The
 * subscription is removed when the scene emits 'shutdown'.
 */
export function bindText<GameKey extends string>(
  i18n: I18n<GameKey>,
  text: Phaser.GameObjects.Text,
  key: GameKey | SharedKey,
  scene: Phaser.Scene,
  params?: Record<string, string | number>,
): Phaser.GameObjects.Text {
  const apply = (): void => {
    text.setText(i18n.t(key, params));
    const wantRTL = i18n.isRTL();
    if (text.style.rtl !== wantRTL) {
      text.setStyle({ rtl: wantRTL });
      text.updateText();
    }
    const script = LANGUAGES.find((l) => l.code === i18n.currentLang())?.script;
    if (script) {
      const family = fontFamilyForScript(script);
      if (text.style.fontFamily !== family) {
        text.setStyle({ fontFamily: family });
        text.updateText();
      }
    }
  };
  apply();
  const off = i18n.subscribe(apply);
  scene.events.once('shutdown', off);
  return text;
}

/**
 * Registers a generic callback to run on every language change. Used by
 * scenes that need to reposition directional UI chrome. Auto-unsubscribes
 * on scene 'shutdown'.
 */
export function onLangChange<GameKey extends string>(
  i18n: I18n<GameKey>,
  scene: Phaser.Scene,
  cb: (lang: LangCode, dir: Direction) => void,
): void {
  const off = i18n.subscribe(cb);
  scene.events.once('shutdown', off);
}
